import React, { memo, useCallback } from 'react'
import { Avatar } from 'transfix-ui/components/Avatar'
import { FunctionalTag, IFunctionalTagOption } from 'transfix-ui/components/FunctionalTag'
import { IconButton } from 'transfix-ui/components/IconButton'
import { OverflowMenu, useOverflowToggle } from 'transfix-ui/components/OverflowMenu'
import { RelativeDateTime } from 'transfix-ui/components/RelativeDateTime'
import { TableRow, TableCell } from 'transfix-ui/components/Table'
import { Tag, TAG_SIZE, TAG_STYLE } from 'transfix-ui/components/Tag'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { Box, Link } from 'transfix-ui/core'
import { ISourcedCarrierRate } from 'types/graphqlTypes'
import { confirmedOptions } from 'apps/marketplace/utils/constants/shipperRates'
import { EllipsisIcon } from 'transfix-icons'
import { useGlobalToast } from 'transfix-ui'
import { getRateTypeLabel } from '../../utils'
import LaneAgreementTag from './LaneAgreementTag'
import { useUpdateConfirmationStatusMutation } from './graphql/UpdateConfirmationStatus'

interface ISourcedCarrierRatesTableRowProps {
  openDeleteSourcedCarrierRateModal: (id: string) => void
  openEditSourcedCarrierRateModal: (sourcedCarrierRate: ISourcedCarrierRate) => void
  sourcedCarrierRate: ISourcedCarrierRate
}

export const SourcedCarrierRatesTableRow = memo<ISourcedCarrierRatesTableRowProps>(
  ({ openDeleteSourcedCarrierRateModal, openEditSourcedCarrierRateModal, sourcedCarrierRate }) => {
    const {
      canDoWeekends,
      carrierApplication,
      confirmed,
      createdAt,
      createdBy,
      id,
      latestLaneAgreement,
      maxCapacity,
      minCapacity,
      rate,
      rateSource,
      updatedAt,
      updatedBy,
    } = sourcedCarrierRate ?? {}
    const { id: carrierApplicationId, name: carrierApplicationName } = carrierApplication ?? {}
    const { amount: rateAmount, rateTypeKey } = rate ?? {}
    const { fullName: createdByFullName } = createdBy ?? {}
    const { fullName: updatedByFullName } = updatedBy ?? {}

    const { openToast } = useGlobalToast()

    const [
      changeConfirmationStatus,
      { loading: changeConfirmationStatusLoading },
    ] = useUpdateConfirmationStatusMutation()

    const handleConfirmedSelect = useCallback(
      async (option: IFunctionalTagOption) => {
        const { value } = option
        const confirmed = value === 'yes'

        try {
          const { data } = await changeConfirmationStatus({
            variables: {
              id,
              confirmed,
            },
          })

          const errorMessages =
            data?.updateSourcedCarrierRate?.errors?.map(err => err.message) ?? []

          if (errorMessages.length > 0) {
            openToast({
              toastMessage: `Failed to update confirmation status: ${errorMessages.join('\n')}`,
              toastType: 'alert',
            })
          } else {
            openToast({
              toastMessage: 'Successfully updated confirmation status.',
              toastType: 'default',
            })
          }
        } catch (err) {
          openToast({
            toastMessage: `Failed to update confirmation status: ${err.message}`,
            toastType: 'alert',
          })
        }
      },
      [id, changeConfirmationStatus, openToast]
    )

    const { clickOverflowTrigger, expanded, setExpanded } = useOverflowToggle()

    const deleteSourcedCarrierRateAction = useCallback(() => {
      openDeleteSourcedCarrierRateModal(id)
    }, [id, openDeleteSourcedCarrierRateModal])

    const editSourcedCarrierRateAction = useCallback(() => {
      openEditSourcedCarrierRateModal(sourcedCarrierRate)
    }, [openEditSourcedCarrierRateModal, sourcedCarrierRate])

    const rateTypeLabel = getRateTypeLabel(rateTypeKey)

    return (
      <TableRow highlightOnHover dataTest={id}>
        <TableCell dataTest={`${id}-carrier`}>
          <Link
            href={`${__ROOT__}/carriers/${carrierApplicationId}`}
            target='_blank'
            rel='noopener noreferrer'>
            {carrierApplicationName}
          </Link>
          {rateSource === 'Bid' && (
            <Box display='inline' ml={1}>
              <Tag label='RFP' size={TAG_SIZE.xsmall} tagStyle={TAG_STYLE.dark} />
            </Box>
          )}
        </TableCell>

        <TableCell dataTest={`${id}-amount`}>
          <div className='body-bold'>${rateAmount}</div>
          <div className='text-secondary body-bold'>{rateTypeLabel}</div>
        </TableCell>

        <TableCell dataTest={`${id}-capacity`}>
          <Box className='body-bold'>
            {minCapacity ? `${minCapacity} - ` : ''}
            {maxCapacity}
            /week
          </Box>

          <Box className='text-secondary body-bold'>{canDoWeekends ? 'Wknds' : 'No Wknds'}</Box>
        </TableCell>

        <TableCell dataTest={`${id}-confirmed`}>
          <FunctionalTag
            isLoading={changeConfirmationStatusLoading}
            options={confirmedOptions}
            value={confirmed ? 'yes' : 'no'}
            size={TAG_SIZE.small}
            onSelect={handleConfirmedSelect}
          />
        </TableCell>

        <TableCell dataTest={`${id}-latest-activity`}>
          <Box display='flex' alignItems='center'>
            <Box mr={2}>
              <Avatar
                name={(updatedAt ? updatedByFullName : createdByFullName) ?? ''}
                size='xsmall'
              />
            </Box>
            <Box>
              <Box>
                <Tooltip
                  id={`${id}-tooltip`}
                  position='top'
                  effect='solid'
                  trigger={<span className='body-bold'>{updatedAt ? 'Updated' : 'Added'}</span>}>
                  <Box textAlign='center'>
                    {updatedAt ? (
                      <>
                        <Box>
                          Created by {createdByFullName}{' '}
                          {createdAt && <RelativeDateTime datetime={createdAt} />}
                        </Box>
                        Last Updated <RelativeDateTime datetime={updatedAt} inSentence />
                      </>
                    ) : (
                      <>{createdAt && <RelativeDateTime datetime={createdAt} inSentence />}</>
                    )}
                  </Box>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </TableCell>

        <TableCell>
          {latestLaneAgreement ? <LaneAgreementTag laneAgreement={latestLaneAgreement} /> : ''}
        </TableCell>

        <TableCell dataTest={`${id}-edit-button`}>
          <OverflowMenu
            onExpanded={setExpanded}
            expanded={expanded}
            dropdownId={String(id) || ''}
            trigger={
              <IconButton
                Icon={EllipsisIcon}
                color='tertiary'
                label='edit'
                data-test='edit-row'
                onClick={clickOverflowTrigger}
                size='large'
              />
            }
            options={[
              { action: editSourcedCarrierRateAction, label: 'Edit Sourced Rate' },
              { action: deleteSourcedCarrierRateAction, label: 'Delete Sourced Rate' },
            ]}
            dataTest={`${id}-edit-overflow-menu`}
          />
        </TableCell>
      </TableRow>
    )
  }
)

export default SourcedCarrierRatesTableRow
